/**
 * Boost streams: short camera-facing additive ribbons trailing the player's boost jets while a fuel-using
 * boost is active (hover/glide flight or boost-run). Same idea as the ship's ThrusterTrails — recent jet
 * positions built into a glowing, tapering streak — but PLAYER-scale (a couple metres long, brightness
 * gated on the boost intensity instead of ship speed). One stream per emit point: the player's two HANDS
 * and two FEET (the old shoulder booster pods are disabled), fed from avatar.boosterStreamSources.
 *
 * History is kept in SYSTEM space (float64) and converted to render space per
 * frame, so floating-origin rebases never kink the streak. Additive blending
 * fades to nothing along the tail.
 */
import * as THREE from "three";

const POINTS = 28; // history depth (samples)
const SAMPLE_DT = 1 / 60; // fixed promotion cadence → trail spans ~POINTS/60 s
const MAX_LEN = 2.6; // visible streak length cap (m) — player scale
const HOVER_JET_LEN = 0.2; // HOVER: a short STATIC straight down-jet (m) instead of the trailing ribbon

class Stream {
  mesh: THREE.Mesh;
  /** History is PLANET-LOCAL (relative to the planet centre + spin), so the streak trails the player over
   *  the SURFACE instead of smearing with the planet's absolute orbit/rotation through space. */
  history: THREE.Vector3[] = [];
  private acc = 0;
  private positions: THREE.BufferAttribute;
  private colors: THREE.BufferAttribute;
  private pts = Array.from({ length: POINTS }, () => new THREE.Vector3()); // render-space path
  private hr = Array.from({ length: POINTS }, () => new THREE.Vector3()); // history → render space scratch
  private _tan = new THREE.Vector3();
  private _toCam = new THREE.Vector3();
  private _side = new THREE.Vector3();

  constructor(scene: THREE.Scene, material: THREE.Material) {
    const geo = new THREE.BufferGeometry();
    this.positions = new THREE.BufferAttribute(new Float32Array(POINTS * 2 * 3), 3);
    // RGBA (4-component): the taper fades via ALPHA under normal blending so the
    // streak is OPAQUE at the head rather than a see-through additive glow.
    this.colors = new THREE.BufferAttribute(new Float32Array(POINTS * 2 * 4), 4);
    geo.setAttribute("position", this.positions);
    geo.setAttribute("color", this.colors);
    const index: number[] = [];
    for (let i = 0; i < POINTS - 1; i++) {
      const a = i * 2;
      index.push(a, a + 1, a + 2, a + 1, a + 3, a + 2);
    }
    geo.setIndex(index);
    this.mesh = new THREE.Mesh(geo, material);
    this.mesh.frustumCulled = false;
    this.mesh.renderOrder = 3;
    this.mesh.visible = false;
    scene.add(this.mesh);
  }

  /** Record the live nozzle position (system space); promote to a fixed sample at 60 Hz. */
  push(sysPos: THREE.Vector3, dt: number): void {
    if (this.history.length === 0) {
      this.history.unshift(sysPos.clone());
      this.acc = 0;
      return;
    }
    this.history[0].copy(sysPos);
    this.acc += dt;
    if (this.acc >= SAMPLE_DT) {
      this.acc = Math.min(this.acc - SAMPLE_DT, SAMPLE_DT);
      this.history.unshift(sysPos.clone());
      if (this.history.length > POINTS) this.history.pop();
    }
  }

  /** Rebuild the camera-facing strip. The PLANET-LOCAL history is rotated/translated by the CURRENT planet
   *  pose (so the whole streak rides the planet), then converted to render space. `k` (0..1) scales width +
   *  brightness; 0 hides it. */
  rebuild(center: THREE.Vector3, quat: THREE.Quaternion, origin: THREE.Vector3, camPos: THREE.Vector3, base: THREE.Color, k: number, maxLen: number): void {
    const n = this.history.length;
    if (n < 2 || k <= 0.001) { this.mesh.visible = false; return; }
    this.mesh.visible = true;
    const pos = this.positions.array as Float32Array;
    const col = this.colors.array as Float32Array;
    const width0 = 0.05 + 0.05 * k;
    // 0) Planet-local history → render space, via the CURRENT planet pose (the trail rides the surface).
    for (let i = 0; i < n; i++) this.hr[i].copy(this.history[i]).applyQuaternion(quat).add(center).sub(origin);
    // 1) March back from the nozzle, capping each step so the streak never exceeds maxLen.
    const step = maxLen / (POINTS - 1);
    this.pts[0].copy(this.hr[0]);
    for (let i = 1; i < POINTS; i++) {
      const newer = this.hr[Math.min(i - 1, n - 1)];
      const older = this.hr[Math.min(i, n - 1)];
      const dx = older.x - newer.x, dy = older.y - newer.y, dz = older.z - newer.z;
      const d = Math.sqrt(dx * dx + dy * dy + dz * dz);
      const p = this.pts[i - 1];
      if (d < 1e-9) this.pts[i].copy(p);
      else { const s = Math.min(d, step) / d; this.pts[i].set(p.x + dx * s, p.y + dy * s, p.z + dz * s); }
    }
    // 2) Build the camera-facing strip with a tapering width + tail fade.
    const last = POINTS - 1;
    for (let i = 0; i < POINTS; i++) {
      this._tan.copy(this.pts[Math.max(0, i - 1)]).sub(this.pts[Math.min(last, i + 1)]);
      if (this._tan.lengthSq() < 1e-9) this._tan.set(0, 0, 1);
      this._tan.normalize();
      this._toCam.copy(camPos).sub(this.pts[i]);
      this._side.crossVectors(this._tan, this._toCam);
      if (this._side.lengthSq() < 1e-9) this._side.set(1, 0, 0);
      this._side.normalize();
      const t = i / last;
      const w = width0 * (1 - t);
      // OPAQUE body: alpha holds near `k` and only tapers over the last ~35%.
      const bri = k * Math.min(1, (1 - t) / 0.35);
      const a = this.pts[i];
      const j = i * 6;
      pos[j] = a.x + this._side.x * w; pos[j + 1] = a.y + this._side.y * w; pos[j + 2] = a.z + this._side.z * w;
      pos[j + 3] = a.x - this._side.x * w; pos[j + 4] = a.y - this._side.y * w; pos[j + 5] = a.z - this._side.z * w;
      const cj = i * 8;
      col[cj] = base.r; col[cj + 1] = base.g; col[cj + 2] = base.b; col[cj + 3] = bri;
      col[cj + 4] = base.r; col[cj + 5] = base.g; col[cj + 6] = base.b; col[cj + 7] = bri;
    }
    this.positions.needsUpdate = true;
    this.colors.needsUpdate = true;
  }

  /** HOVER jet: a STATIC short straight line shooting straight DOWN (toward the planet centre) from the
   *  nozzle — built fresh from the LIVE nozzle position, no trailing history. While hovering the player is
   *  near-stationary, so the history-marched ribbon collapses into a jagged near-zero-length kink; this
   *  draws a clean down-blast instead. Same camera-facing strip + taper/fade as `rebuild`. */
  rebuildHoverJet(nozzle: THREE.Vector3, center: THREE.Vector3, origin: THREE.Vector3, camPos: THREE.Vector3, base: THREE.Color, k: number): void {
    if (k <= 0.001) { this.mesh.visible = false; return; }
    this.mesh.visible = true;
    const pos = this.positions.array as Float32Array;
    const col = this.colors.array as Float32Array;
    const width0 = 0.05 + 0.05 * k;
    // "down" = from the nozzle toward the planet centre, in render space (center − origin is the render-space centre).
    this._tan.set(center.x - origin.x - nozzle.x, center.y - origin.y - nozzle.y, center.z - origin.z - nozzle.z);
    if (this._tan.lengthSq() < 1e-9) this._tan.set(0, -1, 0);
    this._tan.normalize();
    // The tangent is constant along a straight line, so the camera-facing side vector is computed ONCE.
    this._toCam.copy(camPos).sub(nozzle);
    this._side.crossVectors(this._tan, this._toCam);
    if (this._side.lengthSq() < 1e-9) this._side.set(1, 0, 0);
    this._side.normalize();
    const last = POINTS - 1;
    for (let i = 0; i < POINTS; i++) {
      const t = i / last;
      const dlen = t * HOVER_JET_LEN; // march straight DOWN from the nozzle (head) to the tail
      const ax = nozzle.x + this._tan.x * dlen, ay = nozzle.y + this._tan.y * dlen, az = nozzle.z + this._tan.z * dlen;
      const w = width0 * (1 - t);
      // OPAQUE body: alpha holds near `k` and only tapers over the last ~35%.
      const bri = k * Math.min(1, (1 - t) / 0.35);
      const j = i * 6;
      pos[j] = ax + this._side.x * w; pos[j + 1] = ay + this._side.y * w; pos[j + 2] = az + this._side.z * w;
      pos[j + 3] = ax - this._side.x * w; pos[j + 4] = ay - this._side.y * w; pos[j + 5] = az - this._side.z * w;
      const cj = i * 8;
      col[cj] = base.r; col[cj + 1] = base.g; col[cj + 2] = base.b; col[cj + 3] = bri;
      col[cj + 4] = base.r; col[cj + 5] = base.g; col[cj + 6] = base.b; col[cj + 7] = bri;
    }
    this.positions.needsUpdate = true;
    this.colors.needsUpdate = true;
  }

  dispose(scene: THREE.Scene): void {
    scene.remove(this.mesh);
    this.mesh.geometry.dispose();
  }
}

export class BoostStreams {
  private streams: Stream[];
  private material: THREE.MeshBasicMaterial;
  private color = new THREE.Color(0x66ccff);
  private _src: THREE.Vector3[] = [];

  constructor(private scene: THREE.Scene, count = 4) { // 4 emit points: both hands + both feet
    this.material = new THREE.MeshBasicMaterial({
      vertexColors: true,
      blending: THREE.NormalBlending,
      transparent: true,
      depthWrite: false,
      side: THREE.DoubleSide,
    });
    this.streams = Array.from({ length: count }, () => new Stream(scene, this.material));
  }

  /** Tint the streams (e.g. the player's accent colour). */
  setColor(c: THREE.Color): void {
    this.color.copy(c).lerp(new THREE.Color(0xffffff), 0.35);
  }

  /**
   * Per frame. `sources` = render-space nozzle positions, `srcCount` how many are live (0 ⇒ retracted),
   * `k` = 0..1 boost intensity. `planet` = the surface the player stands on (its live pose) — history is
   * stored PLANET-LOCAL so the streak rides the surface, not the planet's absolute orbit/spin. Pass the
   * live origin + camera so render conversion is float-origin-stable and the ribbon faces the camera.
   */
  update(
    sources: THREE.Vector3[],
    srcCount: number,
    planet: { center: THREE.Vector3; quat: THREE.Quaternion; invQuat: THREE.Quaternion } | null,
    origin: THREE.Vector3,
    camPos: THREE.Vector3,
    k: number,
    dt: number,
    hover = false,
    /** Visible streak length cap (m). Defaults to the player scale; the ground
     * vehicle passes a longer, speed-scaled length so the trail reads at speed. */
    maxLen = MAX_LEN,
  ): void {
    for (let i = 0; i < this.streams.length; i++) {
      const s = this.streams[i];
      if (planet && i < srcCount && k > 0.001) {
        if (hover) {
          // HOVERING: drop history (so no jagged near-stationary ribbon, and a clean fresh trail when flight
          // resumes) and draw a short STATIC down-jet straight toward the planet centre from the live nozzle.
          s.history.length = 0;
          s.rebuildHoverJet(sources[i], planet.center, origin, camPos, this.color, k);
          continue;
        }
        if (!this._src[i]) this._src[i] = new THREE.Vector3();
        // render-space nozzle → system space → PLANET-LOCAL (de-orbit / de-spin) before recording.
        this._src[i].copy(sources[i]).add(origin).sub(planet.center).applyQuaternion(planet.invQuat);
        s.push(this._src[i], dt);
        s.rebuild(planet.center, planet.quat, origin, camPos, this.color, k, maxLen);
      } else {
        s.history.length = 0; // retracted → drop history so a stale streak can't snap on next deploy
        s.mesh.visible = false;
      }
    }
  }

  setVisible(v: boolean): void {
    if (!v) for (const s of this.streams) s.mesh.visible = false;
  }

  dispose(): void {
    for (const s of this.streams) s.dispose(this.scene);
    this.material.dispose();
  }
}
